import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Bell, Clock3, FileText, Info, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  EVENT_TYPE_LABELS, EVENT_TYPE_VALUES, EVENT_STATUS_LABELS, RECURRENCE_LABELS,
  type CalendarEvent, type CalendarEventCreate, type CalendarEventType,
  type CalendarEventStatus, type CalendarRecurrence,
} from "@cold-crew/shared";

interface EventoFormProps {
  defaultValues?: Partial<CalendarEvent>;
  onSubmit: (data: CalendarEventCreate) => Promise<void>;
  isLoading?: boolean;
  submitLabel?: string;
}

const inputCls = cn(
  "h-11 w-full rounded-xl border border-[#ded9cf] bg-[#f8f6f1] px-3.5 text-sm text-[#171614]",
  "placeholder:text-[#99948b] focus:outline-none focus:ring-2 focus:ring-[#ff641f]/20 focus:border-[#ff641f]",
  "transition-colors disabled:opacity-60"
);
const labelCls = "block text-[12px] font-bold text-[#3f3c37] mb-1.5";
const today = () => new Date().toISOString().slice(0, 10);

function SectionTitle({ icon: Icon, title, description }: { icon: typeof FileText; title: string; description: string }) {
  return <div className="flex items-start gap-3"><div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#fff0e8] text-[#f45b22]"><Icon className="size-4" /></div><div><h2 className="text-sm font-extrabold text-[#171614]">{title}</h2><p className="mt-0.5 text-xs text-[#817c73]">{description}</p></div></div>;
}

export function EventoForm({ defaultValues, onSubmit, isLoading, submitLabel = "Guardar evento" }: EventoFormProps) {
  const navigate = useNavigate();
  const [titulo, setTitulo] = useState(defaultValues?.titulo ?? "");
  const [descripcion, setDescripcion] = useState(defaultValues?.descripcion ?? "");
  const [fecha, setFecha] = useState(defaultValues?.fecha ?? today());
  const [hora_inicio, setHoraInicio] = useState(defaultValues?.hora_inicio ?? "");
  const [hora_fin, setHoraFin] = useState(defaultValues?.hora_fin ?? "");
  const [tipo, setTipo] = useState<CalendarEventType>(defaultValues?.tipo ?? "otro");
  const [status, setStatus] = useState<CalendarEventStatus>(defaultValues?.status ?? "pendiente");
  const [todo_el_dia, setTodoElDia] = useState(defaultValues?.todo_el_dia ?? false);
  const [recurrencia, setRecurrencia] = useState<CalendarRecurrence>(defaultValues?.recurrencia ?? "none");
  const [recurrenciaFin, setRecurrenciaFin] = useState(defaultValues?.recurrencia_fin ?? "");
  const [recordatorioEmail, setRecordatorioEmail] = useState(defaultValues?.recordatorio_email ?? false);
  const [recordatorioAnticipacion, setRecordatorioAnticipacion] = useState(
    defaultValues?.recordatorio_anticipacion ?? 1440
  );
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    if (!titulo.trim()) return setError("El título es requerido.");
    if (!todo_el_dia && hora_inicio && hora_fin && hora_fin < hora_inicio) return setError("La hora de fin no puede ser anterior a la hora de inicio.");
    if (recurrencia !== "none" && recurrenciaFin && recurrenciaFin < fecha) return setError("La fecha de finalización de la repetición no puede ser anterior al evento.");
    try {
      await onSubmit({
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || undefined,
        fecha,
        hora_inicio: todo_el_dia ? undefined : (hora_inicio || undefined),
        hora_fin: todo_el_dia ? undefined : (hora_fin || undefined),
        tipo,
        status,
        todo_el_dia,
        recurrencia,
        recurrencia_fin: recurrencia !== "none" ? (recurrenciaFin || undefined) : undefined,
        recordatorio_email: recordatorioEmail,
        recordatorio_anticipacion: recordatorioEmail
          ? recordatorioAnticipacion
          : undefined,
      });
    } catch (err) { setError(err instanceof Error ? err.message : "Error al guardar el evento."); }
  }

  const repeatText = recurrencia === "monthly" ? `Se repetirá el día ${Number(fecha.slice(8,10))} de cada mes.` : recurrencia === "weekly" ? "Se repetirá cada semana a partir de esta fecha." : recurrencia === "yearly" ? "Se repetirá cada año en esta fecha." : "Este evento ocurrirá una sola vez.";

  return <form onSubmit={handleSubmit} className="space-y-4">
    <section className="rounded-2xl border border-[#ded9cf] bg-white p-4 sm:p-5 space-y-5">
      <SectionTitle icon={FileText} title="Información" description="Datos principales del evento o recordatorio." />
      <div><label className={labelCls}>Título *</label><input value={titulo} onChange={e=>setTitulo(e.target.value)} placeholder="Título del evento" className={inputCls} disabled={isLoading} required /></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>Tipo *</label><select value={tipo} onChange={e=>setTipo(e.target.value as CalendarEventType)} className={cn(inputCls,"cursor-pointer")} disabled={isLoading}>{EVENT_TYPE_VALUES.map(t=><option key={t} value={t}>{EVENT_TYPE_LABELS[t]}</option>)}</select></div>
        <div><label className={labelCls}>Estado</label><select value={status} onChange={e=>setStatus(e.target.value as CalendarEventStatus)} className={cn(inputCls,"cursor-pointer")} disabled={isLoading}>{(["pendiente","completado","cancelado"] as CalendarEventStatus[]).map(s=><option key={s} value={s}>{EVENT_STATUS_LABELS[s]}</option>)}</select></div>
      </div>
      <div><label className={labelCls}>Descripción <span className="font-normal text-[#8b867d]">(opcional)</span></label><input value={descripcion} onChange={e=>setDescripcion(e.target.value)} placeholder="Notas o descripción del evento" className={inputCls} disabled={isLoading} /></div>
    </section>

    <section className="rounded-2xl border border-[#ded9cf] bg-white p-4 sm:p-5 space-y-5">
      <SectionTitle icon={Clock3} title="Fecha y horario" description="Define cuándo debe aparecer el recordatorio." />
      <div><label className={labelCls}>Fecha *</label><input type="date" value={fecha} onChange={e=>setFecha(e.target.value)} className={inputCls} disabled={isLoading} required /></div>
      <label className="flex items-center gap-3 rounded-xl border border-[#e6e1d8] bg-[#faf8f4] px-3.5 py-3 cursor-pointer"><input type="checkbox" checked={todo_el_dia} onChange={e=>setTodoElDia(e.target.checked)} className="size-4 accent-[#171614]" disabled={isLoading}/><div><p className="text-sm font-semibold text-[#26231f]">Todo el día</p><p className="text-[11px] text-[#8b867d]">Oculta las horas de inicio y fin.</p></div></label>
      {!todo_el_dia && <div className="grid grid-cols-2 gap-3 sm:gap-4"><div><label className={labelCls}>Hora inicio <span className="font-normal text-[#8b867d]">(opcional)</span></label><input type="time" value={hora_inicio} onChange={e=>setHoraInicio(e.target.value)} className={inputCls}/></div><div><label className={labelCls}>Hora fin <span className="font-normal text-[#8b867d]">(opcional)</span></label><input type="time" value={hora_fin} onChange={e=>setHoraFin(e.target.value)} className={inputCls}/></div></div>}
    </section>

    <section className="rounded-2xl border border-[#f0c9b7] bg-gradient-to-br from-white via-[#fffaf6] to-[#fff0e8] p-4 sm:p-5 space-y-5">
      <SectionTitle icon={RefreshCw} title="Repetición" description="Haz que el recordatorio vuelva a aparecer automáticamente." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">{(["none","weekly","monthly","yearly"] as CalendarRecurrence[]).map(r=><button key={r} type="button" onClick={()=>setRecurrencia(r)} className={cn("min-h-12 rounded-xl border px-3 py-2 text-xs font-bold transition-all", recurrencia===r ? "border-[#171614] bg-[#171614] text-white shadow-sm" : "border-[#ded9cf] bg-white text-[#5d5850] hover:border-[#f45b22]")}>{RECURRENCE_LABELS[r]}</button>)}</div>
      <div className="flex gap-2.5 rounded-xl border border-[#f3d7c9] bg-white/80 px-3.5 py-3"><Info className="size-4 shrink-0 text-[#f45b22] mt-0.5"/><p className="text-xs text-[#6d675f]">{repeatText}</p></div>
      {recurrencia !== "none" && <div><label className={labelCls}>Finaliza</label><div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3"><select value={recurrenciaFin ? "date" : "never"} onChange={e=>{if(e.target.value==="never") setRecurrenciaFin(""); else if(!recurrenciaFin) setRecurrenciaFin(fecha)}} className={cn(inputCls,"cursor-pointer")}><option value="never">Nunca</option><option value="date">En una fecha</option></select>{recurrenciaFin && <input type="date" min={fecha} value={recurrenciaFin} onChange={e=>setRecurrenciaFin(e.target.value)} className={inputCls}/>}</div></div>}
    </section>

    <section className="rounded-2xl border border-[#ded9cf] bg-white p-4 sm:p-5 space-y-5">
      <SectionTitle
        icon={Bell}
        title="Recordatorio por correo"
        description="Recibe un aviso antes de que llegue la fecha del evento."
      />

      <label className="flex items-center justify-between gap-4 rounded-xl border border-[#e6e1d8] bg-[#faf8f4] px-3.5 py-3 cursor-pointer">
        <div>
          <p className="text-sm font-semibold text-[#26231f]">
            Enviar recordatorio por correo
          </p>
          <p className="mt-0.5 text-[11px] text-[#8b867d]">
            El sistema enviará automáticamente un aviso por correo.
          </p>
        </div>

        <input
          type="checkbox"
          checked={recordatorioEmail}
          onChange={e => setRecordatorioEmail(e.target.checked)}
          className="size-4 shrink-0 accent-[#171614]"
          disabled={isLoading}
        />
      </label>

      {recordatorioEmail && (
        <div>
          <label className={labelCls}>
            Avisarme
          </label>

          <select
            value={recordatorioAnticipacion}
            onChange={e => setRecordatorioAnticipacion(Number(e.target.value))}
            className={cn(inputCls, "cursor-pointer")}
            disabled={isLoading}
          >
            <option value={5}>5 minutos antes — PRUEBA</option>
            <option value={0}>El mismo día</option>
            <option value={1440}>1 día antes</option>
            <option value={4320}>3 días antes</option>
            <option value={10080}>1 semana antes</option>
          </select>

          <p className="mt-2 text-[11px] text-[#8b867d]">
            Para eventos recurrentes, el aviso se aplicará a cada próxima ocurrencia.
          </p>
        </div>
      )}
    </section>

    {error && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
    <div className="sticky bottom-3 z-10 flex justify-end gap-2 rounded-2xl border border-[#ded9cf] bg-white/95 p-3 shadow-[0_12px_30px_rgba(30,25,20,.10)] backdrop-blur sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
      <button type="button" onClick={()=>navigate({to:"/calendario"})} disabled={isLoading} className="h-10 rounded-xl border border-[#d9d4cb] bg-white px-4 text-sm font-semibold text-[#4c4842] hover:bg-[#f7f4ee]">Cancelar</button>
      <button type="submit" disabled={isLoading} className="h-10 rounded-xl bg-[#090909] px-5 text-sm font-bold text-white hover:opacity-90 disabled:opacity-60">{isLoading ? "Guardando…" : submitLabel}</button>
    </div>
  </form>;
}
